import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { DataTable, MobileRows } from 'ui-kit';
import SortMenu from 'modules/SortMenu';
import SortableHeader from 'modules/SortMenu/SortableHeader';
import useSortTable from 'utils/old/useSortTable';
import ColumnPicker from './ColumnPicker';
import useColumnVisibility from './useColumnVisibility';

/**
 * New-UI list table shared across the redesigned list pages. Wraps the presentational <DataTable />
 * and adds the bits every list needs identically:
 *  - sortable column headers (click to toggle) with `aria-sort` on the <th> for screen readers,
 *  - a single global "Sort:" dropdown injected into the toolbar (the only sort affordance < 1024px,
 *    where the table collapses to header-less cards),
 *  - composite mobile cards (defaults to <MobileRows/>; pass `mobile` to override, e.g. a bespoke card).
 *
 * Pages supply a column array ({ key, label, sortKey?, width?, center?, mobileBlock?, render }) and
 * the sort state; map view / footer / toolbar stay slot-based so each page keeps full control.
 *
 * `sortOption`/`sortHandler` are optional: a list that isn't server-ordered (e.g. the offline
 * staged assignments, held whole in IndexedDB) simply gives its columns no `sortKey`, and gets the
 * card, the mobile layout and the toolbar without a sort affordance.
 */
const ListTable = ({
  columns,
  data,
  rowKey,
  sortOption,
  sortHandler,
  toolbar,
  footer,
  view,
  mapSlot,
  mobile,
  emptyText,
  showColumnPicker,
  scrollable,
  minWidth,
}) => {
  const handleSort = useSortTable(sortOption, sortHandler);
  const { visibleColumns, pickerItems, toggle, hasPicker } = useColumnVisibility(columns);
  // No handler -> no sort affordance anywhere, whatever the columns declare.
  const sortable = typeof sortHandler === 'function';

  const tableColumns = visibleColumns.map((col) => {
    if (!sortable || !col.sortKey) return col;
    const isActive = sortOption.sortBy === col.sortKey;
    return {
      ...col,
      ariaSort: isActive ? (sortOption.sortOrder === 1 ? 'ascending' : 'descending') : 'none',
      header: (
        <SortableHeader label={col.label} sortKey={col.sortKey} sortOption={sortOption} handleSort={handleSort} />
      ),
    };
  });

  const sortFields = sortable
    ? visibleColumns.filter((col) => col.sortKey).map((col) => ({ label: col.label, value: col.sortKey }))
    : [];
  const sortControl = view !== 'map' && sortFields.length > 0 && (
    <SortMenu variant="global" fields={sortFields} sortOption={sortOption} onSort={sortHandler} />
  );
  const columnControl = showColumnPicker && view !== 'map' && hasPicker && (
    <ColumnPicker items={pickerItems} onToggle={toggle} />
  );
  const tableToolbar =
    sortControl || columnControl
      ? {
          ...toolbar,
          lead: (
            <>
              {toolbar?.lead}
              {sortControl}
              {columnControl}
            </>
          ),
        }
      : toolbar;

  const mobileNode = mobile || (
    <MobileRows columns={visibleColumns} data={data} rowKey={rowKey} emptyText={emptyText} />
  );

  return (
    <DataTable
      columns={tableColumns}
      data={data}
      rowKey={rowKey}
      emptyText={emptyText}
      toolbar={tableToolbar}
      footer={footer}
      view={view}
      mapSlot={mapSlot}
      mobile={mobileNode}
      scrollable={scrollable}
      minWidth={minWidth}
    />
  );
};

ListTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  rowKey: PropTypes.func.isRequired,
  sortOption: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.number }),
  sortHandler: PropTypes.func,
  toolbar: PropTypes.object,
  footer: PropTypes.node,
  view: PropTypes.oneOf(['list', 'map']),
  mapSlot: PropTypes.node,
  mobile: PropTypes.node,
  emptyText: PropTypes.node,
  // Opt-in: show the "Columns" show/hide control in the toolbar.
  showColumnPicker: PropTypes.bool,
  // Opt-in horizontal scroll + table min-width so columns don't collapse on narrow desktops.
  scrollable: PropTypes.bool,
  minWidth: PropTypes.string,
};

ListTable.defaultProps = {
  data: [],
  sortOption: { sortBy: '', sortOrder: 1 },
  sortHandler: undefined,
  toolbar: null,
  footer: null,
  view: 'list',
  mapSlot: null,
  mobile: null,
  emptyText: 'No records found.',
  showColumnPicker: false,
  scrollable: false,
  minWidth: undefined,
};

export default memo(ListTable);
